import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Delete, Edit } from '@mui/icons-material'
import { Box, Card, CardActions, CardContent, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import { FtueVideo } from '../../../../../api/core'
import { formatReleaseDate, getFtueCardThumbnail, isVisibleToCustomers } from '../../helpers/ftueVideoHelpers'

type FtueVideoCardProps = {
  video: FtueVideo
  onEdit: (video: FtueVideo) => void
  onDelete: (video: FtueVideo) => void
}

export const FtueVideoCard: FC<FtueVideoCardProps> = ({ video, onEdit, onDelete }) => {
  const { t } = useTranslation()
  const thumbnail = getFtueCardThumbnail(video)
  const name = video.names?.en || '---'
  return (
    <Card data-testid="ftue-video-card" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {thumbnail ? (
        <Box component="img" src={thumbnail.link} alt={name} sx={{ display: 'block', width: '100%', aspectRatio: '295 / 166', objectFit: 'cover' }} />
      ) : (
        <Box sx={{ width: '100%', aspectRatio: '295 / 166', bgcolor: 'action.hover' }} />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography fontWeight="bold" gutterBottom>
          {name}
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:ftue_videos_card_details', {
              version: video.gameVersion || '---',
              market: video.marketIso || '---',
              date: formatReleaseDate(video.gameVersionReleaseDate),
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:ftue_videos_chapter_count', { count: video.chapters?.length ?? 0 })}
          </Typography>
          {!isVisibleToCustomers(video) && (
            <Box>
              <Chip size="small" color="warning" label={t('internal-analysts:ftue_videos_not_visible')} />
            </Box>
          )}
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Tooltip title={t('internal-analysts:ftue_videos_edit')}>
          <IconButton aria-label={t('internal-analysts:ftue_videos_edit_named', { name })} onClick={() => onEdit(video)}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('internal-analysts:ftue_videos_delete')}>
          <IconButton aria-label={t('internal-analysts:ftue_videos_delete_named', { name })} color="error" onClick={() => onDelete(video)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  )
}
